import {Component, EventEmitter, Input, Output} from '@angular/core';
import { AnnualSchoolProfile } from '../interfaces/annual-school-profile';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import 'firebase/firestore';
import {AsyncPipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {AnnualSchoolProfilesFormCardComponent} from "../annual-school-profiles-form-card/annual-school-profiles-form-card.component";
import {getAuth} from "firebase/auth";
import {addDoc, collection, doc, Firestore, getDoc, getDocs, where} from "@angular/fire/firestore";
import {query} from "firebase/firestore";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {TeacherService} from "../services/teacher.service";
import {Teacher} from "../interfaces/teacher";

@Component({
  selector: 'app-annual-school-profiles-form',
  templateUrl: './annual-school-profiles-form.component.html',
  standalone: true,
    imports: [
        AsyncPipe,
        FormsModule,
        NgForOf,
        NgIf,
        NgClass,
        AnnualSchoolProfilesFormCardComponent,
        RouterLink
    ],
  styleUrl: './annual-school-profiles-form.component.css'
})
export class AnnualSchoolProfilesFormComponent {
  @Input() isEditMode: boolean = false;
  @Input() profileId?: string;
  now = new Date();
  teacher: Teacher | null = null;

  formData: AnnualSchoolProfile = {
    project: [] as firebase.firestore.DocumentReference[],
    school: '',
    schoolYear: '',
    ghsRepresentativeName: '',
    ghsRepresentativeEmail: '',
    additionalGhsRepresentativeName: [] as string[],
    additionalGhsRepresentativeEmail: [] as string[],
    numOfStudents: 0,
    numOfTeacherAndStaff: 0,
    studentServingOnGreenYouth: '',
    goals: [] as string[],
    goalsComment: '',
    isDeleted: false,
    submissionDate: this.now.toISOString(),
    editedDate: this.now.toISOString(),
    createdBy: '',
    updatedBy: '',
    isWithinCityLimits: ''
  };

  otherSchoolSelected = false;
  otherTeacherNameSelected = false;
  schools: any[] = [];
  teacherFullList: any[] = [];
  teacherName: any[] = [];
  goals: any[] = [];
  goalsYr= ['Food Waste', 'Gardens', 'Water Pollution & Conservation', 'Recycling', 'Energy', 'Other Goal Category'];
  numOfAdditionalRep = 0;
  errorMessage = '';
  wasAddClicked = false;
  schoolYears: string[] = [];
  annualSchoolProfile: any[] = [];
  ghsRepresentativeList: any[] = [];
  currentDraft: AnnualSchoolProfile | null = null;

  generateSchoolYears() {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    this.schoolYears = [];
    for (let start = 2020; start <= currentYear; start++) {
      const end = start + 1;
      if (end <= nextYear) {
        this.schoolYears.push(`${start}-${end}`);
      }
    }
  }

  @Output() formSubmitted = new EventEmitter<AnnualSchoolProfile>();

  constructor(public afAuth: AngularFireAuth, private firestore: Firestore,
              private router: Router, private teacherService: TeacherService,
              private route: ActivatedRoute) {}

  async ngOnInit() {
    await this.getSchools();
    await this.loadTeachers()
    this.generateSchoolYears();
    await this.getAnnualSchoolProfile();
    this.teacher = await this.teacherService.getTeacher();
    const draft = history.state?.draftProfile;

    if (draft) {
      this.formData = { ...this.formData, ...draft };
    }
    const draftId = this.route.snapshot.queryParamMap.get('draftId');
    if (draftId) {
      await this.loadDraft(draftId);
    }
  }

  async loadDraft(draftId: string) {
    try {
      const docRef = doc(this.firestore, 'annualSchoolProfile', draftId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        this.currentDraft = { id: docSnap.id, ...docSnap.data() } as AnnualSchoolProfile;
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  }

  async loadTeachers() {
    // Teachers filtered by role (for the main GHS rep)
    const teachersByRole = await this.teacherService.getTeachersByRole() ?? [];
    // All teachers (for additional reps)
    const allTeachers = await this.teacherService.getAllTeachers() ?? [];
    this.teacherFullList = allTeachers.map(t => ({
      ...t,
      fullName: `${t.fName} ${t.lName} Teacher`,
    }));
    this.ghsRepresentativeList = teachersByRole.map(t => ({
      ...t,
      fullName: `${t.fName} ${t.lName} Teacher`,
    }));
    this.filterTeacher();
  }

  onSchoolChanged() {
    this.otherSchoolSelected = this.formData.school === 'other';
  }

  async getSchools() {
    const snapshot = await firebase.firestore().collection('school').get();
    this.schools = snapshot.docs.map(doc => doc.data());
    this.schools.forEach((s: any) => s.name += " School");
    this.schools.sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  filterTeacher() {
    const ghsName = this.formData.ghsRepresentativeName;
    const addName = this.formData.additionalGhsRepresentativeName;

    this.teacherName = this.teacherFullList
      .filter(t => t.fullName !== ghsName && !addName.includes(t.fullName))
      .sort((a: any, b: any) => a.fName.localeCompare(b.fName));
  }

  getFilteredTeachersForAdditional(index: number): any[] {
    const selected = this.formData.additionalGhsRepresentativeName[index];
    const otherSelected = [
      this.formData.ghsRepresentativeName,
      ...this.formData.additionalGhsRepresentativeName.filter((_, idx) => idx !== index)
    ];
    return this.teacherFullList
      .filter(t => t.fullName === selected || !otherSelected.includes(t.fullName))
      .sort((a, b) => a.fName.localeCompare(b.fName));
  }


  onTeacherNameChanged() {
    this.otherTeacherNameSelected = this.formData.ghsRepresentativeName === 'other';
    const selectedTeacher = this.teacherName.find(
      t => t.fullName === this.formData.ghsRepresentativeName
    );

    if (selectedTeacher) {
      this.formData.ghsRepresentativeEmail = selectedTeacher.email;
      this.filterTeacher();
    } else {
      this.formData.ghsRepresentativeEmail = '';
    }
  }

  onAdditionalRepNameChanged(index: number) {
    const selectedTeacher = this.teacherFullList.find(
      t => t.fullName === this.formData.additionalGhsRepresentativeName[index]
    );
    if (selectedTeacher) {
      this.formData.additionalGhsRepresentativeEmail[index] = selectedTeacher.email;
      this.filterTeacher();
    } else {
      this.formData.additionalGhsRepresentativeEmail[index] = '';
    }
  }

  toggleGoalSelection(goal: string, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      this.formData.goals.push(goal);
    } else {
      this.formData.goals = this.formData.goals.filter(
        goals => goals !== goal
      );
    }
  }

  addAdditionalRep(){
    if(this.numOfAdditionalRep < 5){
      this.numOfAdditionalRep += 1;
      this.wasAddClicked = true;
    }
    else{
      this.errorMessage = "You can only add up to 5 representatives.";
    }
  }

  removeAdditionalRep(index: number) {
    if (this.numOfAdditionalRep > 0) {
      this.numOfAdditionalRep -= 1;
      this.formData.additionalGhsRepresentativeName.splice(index, 1);
      this.formData.additionalGhsRepresentativeEmail.splice(index, 1);
      this.errorMessage = "";
    } else {
      this.errorMessage = "There are no additional representatives to remove.";
    }
  }

  async getAnnualSchoolProfile(){
    const snapshot = await firebase.firestore().collection('annualSchoolProfile').get();
    this.annualSchoolProfile = snapshot.docs.map(doc => doc.data());
    this.annualSchoolProfile.forEach((s: any)=> s.school += s.schoolYear);
  }

  async submitForm() {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      return alert("You must be logged in to submit this form.");
    }

    try {
      const formCollection = collection(this.firestore, 'annualSchoolProfile');
      const currentTime = new Date().toISOString();

      const dataToSave: any = {
        ...this.formData,
        editedDate: currentTime,
        numOfStudents: this.formData.numOfStudents,
        numOfTeacherAndStaff: this.formData.numOfTeacherAndStaff,
        submissionDate: currentTime,
        createdBy: `${this.teacher?.fName ?? ''} ${this.teacher?.lName ?? ''}`.trim(),
        updatedBy: `${this.teacher?.fName ?? ''} ${this.teacher?.lName ?? ''}`.trim(),
      };

      if (this.isEditMode && this.profileId) {
        // Update existing document
        const docRef = firebase.firestore().collection('annualSchoolProfile').doc(this.profileId);

        const existingDoc = await docRef.get();
        if (existingDoc.exists) {
          dataToSave['submissionDate'] = existingDoc.data()?.['submissionDate'] || currentTime;
          dataToSave['updatedBy'] = existingDoc.data()?.['updatedBy'] || `${this.teacher?.fName ?? ''} ${this.teacher?.lName ?? ''}`.trim();
        } else {
          dataToSave['submissionDate'] = currentTime;
          dataToSave['updatedBy'] = `${this.teacher?.fName ?? ''} ${this.teacher?.lName ?? ''}`.trim();
        }

        await docRef.update(dataToSave);
        alert('Profile updated successfully!');
        this.router.navigate([`/annual-school-profiles/${this.profileId}`]);

      } else {
        // Create new document
        const q = query(
          formCollection,
          where('school', '==', this.formData.school),
          where('schoolYear', '==', this.formData.schoolYear)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          alert('A profile for this school and year already exists.');
          return;
        }

        dataToSave['submissionDate'] = currentTime;
        const docRef = await addDoc(formCollection, dataToSave);
        alert('Form submitted successfully!');
        this.router.navigate([`/annual-school-profiles/${docRef.id}`]);
      }

      this.formSubmitted.emit(this.formData);

    } catch (error: any) {
      alert(`Error submitting form: ${error.message}`);
      console.error(error);
    }
  }

  initializeForm(profile: AnnualSchoolProfile) {
    this.formData = {
      ...profile,
      additionalGhsRepresentativeName: [...(profile.additionalGhsRepresentativeName || [])],
      additionalGhsRepresentativeEmail: [...(profile.additionalGhsRepresentativeEmail || [])],
      goals: [...(profile.goals || [])],
    };

    if (this.formData.additionalGhsRepresentativeName?.length) {
      this.numOfAdditionalRep = this.formData.additionalGhsRepresentativeName.length;
      this.wasAddClicked = true;
    }
  }
}
